import React, { useState, useMemo, useEffect } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle, 
  TrendingUp, 
  Zap, 
  Shield, 
  Settings, 
  Calendar, 
  Target, 
  Download, 
  Copy,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Database,
  Users,
  ExternalLink,
  BarChart3,
  MapPin
} from 'lucide-react';
import { selectRecommendation } from '../../adapters/profile/selectors';

// Decision Matrix Component
const DecisionMatrix = ({ roiRange, readiness, complexity }) => {
  const getReadinessScore = (readiness) => {
    const scores = { 'Low': 20, 'Medium': 50, 'High': 80 };
    return scores[readiness] || 50;
  };

  const getComplexityScore = (complexity) => {
    const scores = { 'Low': 80, 'Medium': 50, 'High': 20 };
    return scores[complexity] || 50;
  };

  const feasibilityScore = (getReadinessScore(readiness) + getComplexityScore(complexity)) / 2;
  const valueScore = roiRange?.includes('High') ? 75 : roiRange?.includes('Medium') ? 50 : 25;

  const getPositionLabel = () => {
    if (valueScore >= 60 && feasibilityScore >= 60) return 'High value / High feasibility';
    if (valueScore >= 60 && feasibilityScore < 60) return 'High value / Medium feasibility';
    if (valueScore < 60 && feasibilityScore >= 60) return 'Medium value / High feasibility';
    return 'Medium value / Medium feasibility';
  };

  return (
    <div className="relative w-full h-48 bg-gradient-to-tr from-red-50 via-yellow-50 to-green-50 rounded-lg border border-gray-200 p-4">
      <div className="absolute top-2 left-2 text-xs font-medium text-gray-600">Low Value</div>
      <div className="absolute top-2 right-2 text-xs font-medium text-gray-600">High Value</div>
      <div className="absolute bottom-2 left-2 text-xs font-medium text-gray-600">Low Feasibility</div>
      <div className="absolute bottom-2 right-2 text-xs font-medium text-gray-600">High Feasibility</div>
      
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-full h-px bg-gray-300"></div>
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-px h-full bg-gray-300"></div>
      </div>
      
      <div 
        className="absolute w-4 h-4 bg-[#FF6E4C] rounded-full border-2 border-white shadow-lg transform -translate-x-2 -translate-y-2"
        style={{
          left: `${valueScore}%`,
          top: `${100 - feasibilityScore}%`
        }}
        title={getPositionLabel()}
      />
      
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-xs font-medium text-gray-700 bg-white px-2 py-1 rounded border">
        {getPositionLabel()}
      </div>
      
      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-6 text-xs text-gray-500">
        Feasibility →
      </div>
      <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-6 rotate-90 text-xs text-gray-500">
        Value →
      </div>
    </div>
  );
};

export default function SectionRecommendation({ profileData = {}, section = {}, sections = [], loading = false, error = null, clientId }) {
  const [conditionsChecked, setConditionsChecked] = useState({});
  const [showFullRationale, setShowFullRationale] = useState(false);
  const [showMatrix, setShowMatrix] = useState(false);

  useEffect(() => {
    console.log('🔍 SectionRecommendation rendered');
    // Count recommendation sections in DOM
    const sectionElements = document.querySelectorAll('[id*="recommendation"], [class*="recommendation"]');
    console.log('Found recommendation sections:', sectionElements.length);
    sectionElements.forEach((sectionEl, index) => {
      console.log(`Section ${index + 1}:`, {
        id: sectionEl.id,
        className: sectionEl.className,
        hasConfidenceBadge: sectionEl.textContent.includes('Confidence'),
        firstChars: sectionEl.textContent.substring(0, 50)
      });
    });
  }, []);

  // Create section map for selector
  const sectionMap = {
    'recommendation': 'recommendation',
    'rationale': 'rationale',
    'next_steps': 'next_steps'
  };

  // Extract client sections if available
  const clientSections = sections?.filter(s => s.client_id === clientId) || [];

  // Use selector for recommendation data with proper precedence
  const recommendationModel = selectRecommendation(
    profileData,
    clientSections,
    sectionMap
  );

  // Extract data from selector
  const decision = recommendationModel.status === 'Recommended' ? 'Go' :
                   recommendationModel.status === 'Consider' ? 'Go with Conditions' :
                   recommendationModel.status === 'Monitor' ? 'Not Now' :
                   recommendationModel.status === 'Defer' ? 'Not Now' : 'TBD';
  
  // Use cleaned rationale from selector
  const rationale = recommendationModel.rationale || 'Analysis pending';
  
  // Extract next steps from selector
  const selectorNextSteps = recommendationModel.nextSteps || [];
  
  // Other data remains the same
  const alignmentScore = profileData?.strategic_alignment_score || 0;
  const roiRange = profileData?.roi_range || 'TBD';
  const timeToRoi = profileData?.time_to_roi || profileData?.estimated_time_to_roi || 'TBD';
  const readiness = profileData?.implementation?.readiness || 'Medium';
  const complexity = profileData?.implementation?.complexity || 'Medium';

  const getDecisionConfidence = () => {
    if (alignmentScore >= 80 && readiness === 'High') return 'High';
    if (alignmentScore >= 60 && readiness !== 'Low') return 'Medium';
    return 'Low';
  };

  const confidence = getDecisionConfidence();

  const nextSteps = useMemo(() => {
    // First try selector next steps
    if (selectorNextSteps.length > 0) {
      return selectorNextSteps.slice(0, 3).map((step, idx) => ({
        id: idx + 1,
        label: step,
        dueMonth: `M${idx + 1}`,
        valueNote: '',
        owner: 'TBD',
        completed: false
      }));
    }
    
    // Then fall back to milestones
    if (profileData?.milestones?.length > 0) {
      return profileData.milestones
        .filter(milestone => milestone.months_from_start <= 3)
        .map(milestone => ({
          id: milestone.id || Math.random(),
          label: milestone.label,
          dueMonth: `M${milestone.months_from_start}`,
          valueNote: milestone.value_note,
          owner: 'TBD',
          completed: false
        }));
    }
    
    // Default fallback
    return [
      { id: 1, label: 'Complete stakeholder alignment sessions', dueMonth: 'M1', owner: 'Data Strategy Lead', completed: false },
      { id: 2, label: 'Finalize technical architecture review', dueMonth: 'M2', owner: 'Solutions Architect', completed: false },
      { id: 3, label: 'Execute pilot data integration', dueMonth: 'M3', owner: 'Data Engineering', completed: false }
    ];
  }, [selectorNextSteps, profileData]);

  const gatingDependencies = useMemo(() => {
    const gatingItems = [];
    
    if (profileData?.risks?.length > 0) {
      const criticalRisks = profileData.risks.filter(risk => 
        risk.severity === 'Critical' || risk.severity === 'High'
      );
      criticalRisks.forEach(risk => {
        gatingItems.push({
          id: `risk-${risk.id || Math.random()}`,
          label: risk.risk_description || risk.name,
          details: risk.impact_note || 'Impact assessment pending',
          action: risk.mitigation || 'Mitigation strategy needed',
          type: 'risk',
          severity: risk.severity
        });
      });
    }
    
    if (profileData?.requirements?.length > 0) {
      profileData.requirements.forEach(req => {
        const type = req.req_type?.toLowerCase() || 'system';
        gatingItems.push({
          id: `req-${req.id || Math.random()}`,
          label: req.name,
          details: req.details || 'Requirements details pending',
          action: 'Complete implementation',
          type: type,
          severity: 'High'
        });
      });
    }
    
    if (gatingItems.length === 0) {
      return [
        {
          id: 'mock-1',
          label: 'Data governance framework approval',
          details: 'Establish data stewardship and compliance protocols',
          action: 'Schedule governance committee review',
          type: 'data',
          severity: 'Critical'
        },
        {
          id: 'mock-2', 
          label: 'SFTP data feed validation',
          details: 'Validate secure data transfer capabilities with external partners',
          action: 'Complete technical validation testing',
          type: 'system',
          severity: 'High'
        },
        {
          id: 'mock-3',
          label: 'Dedicated analytics team staffing',
          details: 'Secure 2 FTE data analysts for implementation phase',
          action: 'Finalize hiring and onboarding plan',
          type: 'staffing',
          severity: 'High'
        }
      ];
    }
    
    return gatingItems;
  }, [profileData]);

  if (loading) {
    return (
      <section id="recommendation" aria-labelledby="recommendation-title" className="scroll-mt-24">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="h-6 bg-gray-200 rounded w-64 animate-pulse"></div>
            <div className="flex gap-2">
              <div className="h-8 bg-gray-200 rounded w-24 animate-pulse"></div>
              <div className="h-8 bg-gray-200 rounded w-28 animate-pulse"></div>
            </div>
          </div>
          <div className="flex items-start gap-4 mb-6">
            <div className="h-12 bg-gray-200 rounded-xl w-32 animate-pulse"></div>
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-full mb-2 animate-pulse"></div>
              <div className="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>
            </div>
          </div>
          <div className="flex gap-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-8 bg-gray-200 rounded-lg w-24 animate-pulse"></div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="recommendation" aria-labelledby="recommendation-title" className="scroll-mt-24">
        <div className="bg-white border border-red-200 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h2 className="text-xl font-bold text-[#18365E]">Executive Recommendation</h2>
          </div>
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 font-medium">Unable to load recommendation data</p>
            <p className="text-red-600 text-sm mt-1">{error.message || 'Please try again later'}</p>
          </div>
        </div>
      </section>
    );
  }

  if (!decision || decision === 'TBD') {
    return (
      <section id="recommendation" aria-labelledby="recommendation-title" className="scroll-mt-24">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
          <h2 id="recommendation-title" className="text-xl font-bold text-[#18365E] flex items-center gap-2 mb-4">
            <Target className="w-5 h-5" />
            Executive Recommendation
          </h2>
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Recommendation Pending</h3>
            <p className="text-gray-600 mb-4">
              Complete the analysis in other sections to unlock an evidence-based decision.
            </p>
            <div className="text-sm text-gray-500">
              <p>Required sections: Risks, Feasibility, Roadmap</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const getDecisionStyle = () => {
    switch (decision) {
      case 'Go':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Go with Conditions':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Not Now':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getDecisionIcon = () => {
    switch (decision) {
      case 'Go':
        return <CheckCircle className="w-5 h-5" />;
      case 'Go with Conditions':
        return <AlertTriangle className="w-5 h-5" />;
      case 'Not Now':
        return <XCircle className="w-5 h-5" />;
      default:
        return <Clock className="w-5 h-5" />;
    }
  };

  const handleConditionToggle = (id) => {
    setConditionsChecked(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleExportPDF = () => {
    alert('PDF export functionality coming soon!');
  };

  const handleCopySummary = () => {
    const cleanRationale = rationale || 'Analysis pending';
    const summary = `Executive Recommendation: ${decision}\n\nRationale: ${cleanRationale}\n\nKey Metrics:\n- Alignment: ${alignmentScore}%\n- ROI: ${roiRange}\n- Time to ROI: ${timeToRoi}\n- Readiness: ${readiness}\n- Complexity: ${complexity}\n\nNext Steps:\n${nextSteps.map(step => `- ${step.label} (${step.dueMonth})`).join('\n')}\n\nGating Dependencies:\n${gatingDependencies.map(dep => `- ${dep.label}: ${dep.action}`).join('\n')}`;
    
    navigator.clipboard.writeText(summary).then(() => {
      alert('Summary copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy summary');
    });
  };

  // Main render - PROPERLY FORMATTED EXECUTIVE SUMMARY
  return (
    <section id="recommendation" aria-labelledby="recommendation-title" className="scroll-mt-24">
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 id="recommendation-title" className="text-xl font-bold text-[#18365E] flex items-center gap-2">
            <Target className="w-5 h-5" />
            Executive Recommendation
          </h2>
          <div className="flex items-center gap-2">
            <button 
              onClick={handleExportPDF}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-[#18365E] text-white rounded-lg hover:bg-[#18365E]/90 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export PDF
            </button>
            <button 
              onClick={handleCopySummary}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Copy className="w-4 h-4" />
              Copy Summary
            </button>
          </div>
        </div>

        {/* EXECUTIVE SUMMARY SECTION - PROPERLY FORMATTED FOR 3-SECOND SCAN */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className={`flex items-center gap-3 px-6 py-3 rounded-xl border-2 font-bold text-xl ${getDecisionStyle()}`}>
              {getDecisionIcon()}
              <span>{decision}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1.5 text-sm font-semibold rounded-full ${
                confidence === 'High' ? 'bg-green-100 text-green-800 border border-green-300' :
                confidence === 'Medium' ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' :
                'bg-red-100 text-red-800 border border-red-300'
              }`}>
                {confidence} Confidence
              </span>
              {recommendationModel.source && (
                <span className="text-xs text-gray-500 italic">
                  ({recommendationModel.source} data)
                </span>
              )}
            </div>
          </div>
          
          {/* FORMATTED EXECUTIVE SUMMARY BOX */}
          <div className="bg-white rounded-lg p-6 border border-blue-200">
            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-4">
              Executive Summary
            </h3>
            
            {/* Scannable Executive Bullet Points */}
            <div className="space-y-3">
              {/* Primary Recommendation */}
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <span className="font-semibold text-gray-700">Recommendation: </span>
                  <span className="text-gray-900">
                    {rationale && rationale.length > 150 
                      ? rationale.substring(0, 150).trim() + '...'
                      : rationale}
                  </span>
                </div>
              </div>
              
              {/* Key Value Drivers */}
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <span className="font-semibold text-gray-700">Expected ROI: </span>
                  <span className="text-gray-900">{roiRange} return within {timeToRoi}</span>
                </div>
              </div>
              
              {/* Implementation Readiness */}
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-orange-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <span className="font-semibold text-gray-700">Organizational Readiness: </span>
                  <span className="text-gray-900">{readiness} readiness with {complexity} complexity</span>
                </div>
              </div>
              
              {/* Strategic Alignment */}
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <span className="font-semibold text-gray-700">Strategic Alignment: </span>
                  <span className="text-gray-900">{alignmentScore}% alignment with organizational priorities</span>
                </div>
              </div>
            </div>
            
            {/* View Full Analysis Link */}
            {rationale && rationale.length > 150 && (
              <button 
                onClick={() => setShowFullRationale(!showFullRationale)}
                className="mt-4 text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
              >
                {showFullRationale ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Show Less Detail
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    View Full Analysis
                  </>
                )}
              </button>
            )}
            
            {/* Full Rationale (when expanded) */}
            {showFullRationale && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-600 mb-2">Detailed Analysis</h4>
                <p className="text-gray-700 leading-relaxed">
                  {rationale}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* KEY METRICS STRIP */}
        <div className="grid grid-cols-5 gap-4 mb-8">
          <div className="bg-white border border-blue-200 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Alignment</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">{alignmentScore}%</div>
          </div>
          
          <div className="bg-white border border-green-200 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-green-600" />
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">ROI Range</span>
            </div>
            <div className="text-lg font-bold text-green-600">{roiRange}</div>
          </div>
          
          <div className="bg-white border border-purple-200 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-purple-600" />
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Time to ROI</span>
            </div>
            <div className="text-lg font-bold text-purple-600">{timeToRoi}</div>
          </div>
          
          <div className="bg-white border border-orange-200 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-orange-600" />
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Readiness</span>
            </div>
            <div className="text-lg font-bold text-orange-600">{readiness}</div>
          </div>
          
          <div className="bg-white border border-gray-300 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Settings className="w-5 h-5 text-gray-600" />
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Complexity</span>
            </div>
            <div className="text-lg font-bold text-gray-700">{complexity}</div>
          </div>
        </div>
      </div>

      {/* DECISION MATRIX AND NEXT STEPS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-[#18365E] mb-4 flex items-center gap-2">
            <Target className="w-5 h-5" />
            Decision Matrix
          </h3>
          <DecisionMatrix roiRange={roiRange} readiness={readiness} complexity={complexity} />
          <div className="mt-4 text-xs text-gray-500 text-center">
            Position based on ROI potential vs implementation feasibility
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-[#18365E] mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Next 30-90 Days
          </h3>
          <div className="space-y-3">
            {nextSteps.map((step, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 bg-[#18365E] text-white rounded-full flex items-center justify-center text-xs font-bold">
                  {step.dueMonth}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 text-sm mb-1">{step.label}</div>
                  {step.valueNote && (
                    <div className="text-xs text-gray-600 mb-1">{step.valueNote}</div>
                  )}
                  <div className="text-xs text-blue-600">{step.owner}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-xs text-gray-500">
            Derived from implementation milestones
          </div>
        </div>
      </div>

      {/* ACTION CARDS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-green-800 flex items-center gap-3">
              <Calendar className="w-6 h-6" />
              Immediate Next Steps
            </h3>
            <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-semibold rounded-full">
              {nextSteps.length} Actions
            </span>
          </div>
          
          <div className="space-y-4">
            {nextSteps.slice(0, 4).map((step, idx) => (
              <div key={idx} className="bg-white rounded-xl p-4 border border-green-200 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 ${
                    step.dueMonth === 'M1' ? 'bg-red-50 text-red-700 border-red-300' :
                    step.dueMonth === 'M2' ? 'bg-yellow-50 text-yellow-700 border-yellow-300' :
                    'bg-green-50 text-green-700 border-green-300'
                  }`}>
                    {step.dueMonth}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 mb-1">{step.label}</div>
                    {step.valueNote && (
                      <div className="text-sm text-gray-600 mb-2">{step.valueNote}</div>
                    )}
                    <div className="text-sm text-green-700 font-medium">{step.owner}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-4 text-xs text-green-700 font-medium">
            From implementation milestones
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-orange-800 flex items-center gap-3">
              <Shield className="w-6 h-6" />
              Critical Gating Factors
            </h3>
            <span className="px-3 py-1 bg-orange-100 text-orange-800 text-sm font-semibold rounded-full">
              {gatingDependencies.length} Dependencies
            </span>
          </div>
          
          <div className="space-y-4">
            {gatingDependencies.slice(0, 4).map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl p-4 border border-orange-200 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    item.type === 'risk' ? 'bg-red-100 text-red-700' :
                    item.type === 'data' ? 'bg-blue-100 text-blue-700' :
                    item.type === 'system' ? 'bg-purple-100 text-purple-700' :
                    'bg-orange-100 text-orange-700'
                  }`}>
                    {item.type?.toUpperCase() || 'DEPENDENCY'}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 mb-1">{item.label}</div>
                    {item.action && (
                      <div className="text-sm text-gray-600">{item.action}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-4 text-xs text-orange-700 font-medium">
            Critical risks and requirements
          </div>
        </div>
      </div>

      {/* COLLAPSIBLE DECISION MATRIX */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-6">
        <button 
          onClick={() => setShowMatrix(!showMatrix)}
          className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <h3 className="text-lg font-semibold text-[#18365E] flex items-center gap-2">
            <Target className="w-5 h-5" />
            Decision Matrix & Analysis
          </h3>
          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showMatrix ? 'rotate-180' : ''}`} />
        </button>
        
        {showMatrix && (
          <div className="px-6 pb-6">
            <div className="relative h-48 bg-gray-50 rounded-lg border border-gray-200 p-4">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-4 h-4 bg-blue-600 rounded-full mx-auto mb-2"></div>
                  <div className="text-sm font-medium text-gray-700">Value vs Feasibility</div>
                  <div className="text-xs text-gray-500 mt-1">Positioned based on metrics above</div>
                </div>
              </div>
              <div className="absolute bottom-3 left-3 text-xs text-gray-500">Low Feasibility</div>
              <div className="absolute bottom-3 right-3 text-xs text-gray-500">High Feasibility</div>
              <div className="absolute top-3 left-3 text-xs text-gray-500 transform -rotate-90 origin-left">High Value</div>
              <div className="absolute bottom-16 left-3 text-xs text-gray-500 transform -rotate-90 origin-left">Low Value</div>
            </div>
            <div className="mt-4 text-xs text-gray-500">
              Matrix positioning: ROI ({roiRange}) × Readiness ({readiness}) × Complexity ({complexity})
            </div>
          </div>
        )}
      </div>

      {/* CONDITIONAL APPROVAL SECTION */}
      {decision === 'Go with Conditions' && (
        <div className="bg-white border border-yellow-200 rounded-xl shadow-sm p-6 mb-6">
          <h3 className="text-lg font-semibold text-[#18365E] mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            Conditions to Proceed
          </h3>
          <div className="space-y-3">
            {gatingDependencies.slice(0, 3).map((condition) => (
              <label key={condition.id} className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200 cursor-pointer hover:bg-yellow-100 transition-colors">
                <input
                  type="checkbox"
                  checked={conditionsChecked[condition.id] || false}
                  onChange={() => handleConditionToggle(condition.id)}
                  className="mt-1 w-4 h-4 text-yellow-600 border-yellow-300 rounded focus:ring-yellow-500"
                />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{condition.label}</p>
                  <p className="text-sm text-gray-600 mt-1">{condition.action}</p>
                </div>
              </label>
            ))}
          </div>
          
          {Object.keys(conditionsChecked).length > 0 && Object.values(conditionsChecked).every(Boolean) && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 font-medium flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                All conditions satisfied - Ready to proceed
              </p>
            </div>
          )}
        </div>
      )}

      {/* STICKY FOOTER WITH QUICK ACTIONS */}
      <div className="sticky bottom-0 bg-white border border-gray-200 rounded-xl shadow-lg p-4 mt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <MapPin className="w-4 h-4" />
            <span>Quick Actions</span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowMatrix(!showMatrix)}
              className="flex items-center gap-2 px-4 py-2 bg-[#18365E] text-white rounded-lg hover:bg-[#0f2847] transition-colors text-sm font-medium"
            >
              <BarChart3 className="w-4 h-4" />
              Decision Matrix
            </button>
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-2 px-4 py-2 bg-[#FF6E4C] text-white rounded-lg hover:bg-[#e55a3a] transition-colors text-sm font-medium"
            >
              <Download className="w-4 h-4" />
              Export PDF
            </button>
            <button
              onClick={handleCopySummary}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 bg-white rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              aria-label="Copy recommendation summary"
            >
              <Copy className="w-4 h-4" />
              Copy
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}